import { Component, Input, Output, EventEmitter, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { trigger, transition, style, animate } from '@angular/animations';

@Component({
  selector: 'app-image-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './image-modal.component.html',
  styleUrls: ['./image-modal.component.scss'],
  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('200ms ease-out', style({ opacity: 1 }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ opacity: 0 }))
      ])
    ]),
    trigger('zoomIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(0.8)' }),
        animate('250ms ease-out', style({ opacity: 1, transform: 'scale(1)' }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ opacity: 0, transform: 'scale(0.8)' }))
      ])
    ])
  ]
})
export class ImageModalComponent {
  @Input() isVisible: boolean = false;
  @Input() images: string[] = [];
  @Input() currentIndex: number = 0;
  @Input() alt: string = 'Image';
  @Output() closeModal = new EventEmitter<void>();
  @Output() indexChange = new EventEmitter<number>();

  isZoomed: boolean = false;

  get currentImage(): string {
    return this.images[this.currentIndex] || '';
  }

  get hasMultipleImages(): boolean {
    return this.images.length > 1;
  }

  get canGoPrev(): boolean {
    return this.currentIndex > 0;
  }

  get canGoNext(): boolean {
    return this.currentIndex < this.images.length - 1;
  }

  @HostListener('document:keydown', ['$event'])
  handleKeydown(event: KeyboardEvent) {
    if (!this.isVisible) return;

    switch (event.key) {
      case 'Escape':
        this.close();
        break;
      case 'ArrowLeft':
        this.prev();
        break;
      case 'ArrowRight':
        this.next();
        break;
    }
  }

  close(): void {
    this.isZoomed = false;
    this.closeModal.emit();
  }

  onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('image-modal-backdrop')) {
      this.close();
    }
  }

  prev(): void {
    if (this.canGoPrev) {
      this.currentIndex--;
      this.isZoomed = false;
      this.indexChange.emit(this.currentIndex);
    }
  }

  next(): void {
    if (this.canGoNext) {
      this.currentIndex++;
      this.isZoomed = false;
      this.indexChange.emit(this.currentIndex);
    }
  }

  goToImage(index: number): void {
    if (index >= 0 && index < this.images.length) {
      this.currentIndex = index;
      this.isZoomed = false;
      this.indexChange.emit(this.currentIndex);
    }
  }

  toggleZoom(): void {
    this.isZoomed = !this.isZoomed;
  }

  // Helper method to open modal with single image
  static openSingle(image: string): { images: string[], currentIndex: number } {
    return { images: [image], currentIndex: 0 };
  }

  // Helper method to open modal with multiple images
  static openMultiple(images: string[], startIndex: number = 0): { images: string[], currentIndex: number } {
    return { images, currentIndex: startIndex };
  }
}
